//@flow
import React, {useState, useMemo, useRef, useEffect} from 'react';
import {
  Typography,
  Divider,
  makeStyles,
  ButtonGroup,
  Collapse,
} from '@material-ui/core';
import {
  Edit,
  AddCircleOutline,
  LocalAtm,
  ExpandMore,
  ExpandLess,
} from '@material-ui/icons';
import {FormProvider, useForm} from 'react-hook-form';
import Skeleton from '@material-ui/lab/Skeleton';

import CustomButton from '../../../../elements/CustomButton/CustomButton';
import CustomModal from '../../../../elements/CustomModal/CustomModal';
import InlineLoader from '../../../../elements/LoadingSpinner/InlineLoader';
import TaxInvoiceInformationForm, {
  ORDER_TAX_INVOICE_FORM_FIELDS,
} from '../../../manual-order/components/TaxInvoiceInformationForm';
import InvoiceSection from '../InvoiceSection';

import {colors} from '../../../../assets/styles/colors';
import {ORDER_STATES_FOR_TAX_INVOICE} from '../../../../entities';

import {useOrderTaxInvoice, useCurrentOrderActions} from '../../utils';
import {useStoreCountry} from '../../../country/utils';
import {
  useTaxInvoiceList,
  useTaxInvoiceProviders,
  verifyInvoiceCreation,
} from '../../../tax-invoice/utils';
import {useOrderTaxInvoiceOrderActions} from '../../../tax-invoice/utils';
import {useToastActions} from '../../../toast-alert/utils';

import type {Order} from '../../../../entities';

// Constants for business logic
const MAX_AUTO_REFRESH_TRIES = 10;
const AUTO_REFRESH_INTERVAL_MS = 3000;
const DEFAULT_COUNTRY_CODE = 'PE';
const TAX_INVOICE_TYPE_BOLETA = 'BOLETA';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '8px',
    background: colors.grey100,
    border: `1px solid ${colors.grey200}`,
    padding: theme.spacing(1),
    width: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
  },
  title: {
    color: colors.grey900,
    fontSize: '0.875rem',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  divider: {
    background: colors.grey200,
    width: '100%',
    margin: theme.spacing(1, 0),
  },
  contentText: {
    color: colors.grey800,
    fontSize: '0.875rem',
    margin: '2px 0px',
  },
  bold: {
    fontWeight: 600,
  },
  saveButton: {
    marginTop: '1rem',
    maxWidth: '250px',
  },
  modalContainer: {
    position: 'relative',
  },
  loader: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    left: 0,
    background: 'rgba(255,255,255,0.8)',
    top: 0,
    zIndex: 1,
  },
  noDataText: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: colors.grey600,
  },
  generateCard: {
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${colors.grey300}`,
    borderRadius: '.5rem',
    width: '100%',
    marginTop: theme.spacing(2),
  },
  generateHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '.75rem',
    gap: '.5rem',
  },
  generateHeaderLabel: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 500,
    gap: '.5rem',
    fontSize: '.85rem',
  },
  generateFooter: {
    height: 44,
    borderBlock: `dashed 2px ${colors.grey300}`,
  },
  editButton: {
    color: colors.info,
    '&[disabled]': {
      color: colors.grey600,
    },
  },
  emitButton: {
    color: colors.primary,
    '&[disabled]': {
      color: colors.grey500,
    },
  },
  generateOptionsContainer: {
    display: 'flex',
    width: '100%',
    position: 'relative',
  },
  generateLoader: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    background: 'rgba(255,255,255,0.5)',
    zIndex: 1,
    backdropFilter: 'blur(.25rem)',
  },
  caption: {
    color: colors.grey600,
    fontSize: '0.75rem',
    marginTop: theme.spacing(1),
  },
  invoiceListContainer: {
    marginTop: theme.spacing(2),
    maxHeight: '400px',
    overflowY: 'auto',
  },
  content: {
    paddingTop: theme.spacing(1),
    position: 'relative',
  },
  contentOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(255,255,255,0.6)',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
  },
  skeletonContainer: {
    padding: theme.spacing(1, 0),
  },
  skeletonLine: {
    marginBottom: theme.spacing(0.5),
    borderRadius: '4px',
  },
}));

type Props = {
  order: Order,
};

export default function OrderTaxInvoiceSection(props: Props) {
  const {order} = props;
  const classes = useStyles();

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [savingForm, setSavingForm] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const {
    initialLoading,
    taxInvoiceInformation,
    taxInvoiceList,
    refetch,
  } = useOrderTaxInvoice(order?.id);

  const country = useStoreCountry();
  const {providers} = useTaxInvoiceList();
  const taxInvoiceProviders = useTaxInvoiceProviders();
  const defaultProvider = taxInvoiceProviders.find(
    provider => provider.enabled
  );

  const {showToast} = useToastActions();
  const {updateOrderTaxInvoiceInformation} = useCurrentOrderActions();
  const {editOrderTaxInvoiceData} = useOrderTaxInvoiceOrderActions();
  const {emitOrderTaxInvoice, loading: emitLoading} = useCurrentOrderActions();

  const methods = useForm();
  const triesRef = useRef(0);

  const noTaxInvoice = providers.every(provider => !provider.enabled);

  const canEmitDocument =
    !!taxInvoiceInformation &&
    ORDER_STATES_FOR_TAX_INVOICE.includes(order?.orderState);

  const shouldGenerateAutomatically =
    defaultProvider?.providerConfig?.automaticGeneration &&
    taxInvoiceList.length === 0 &&
    canEmitDocument;

  const canGenerateNewTaxInvoice = useMemo(() => {
    if (!taxInvoiceInformation) return false;
    if (taxInvoiceList.length === 0) return true;
    return verifyInvoiceCreation(taxInvoiceList);
  }, [taxInvoiceInformation, taxInvoiceList]);

  const showGenerateSection = canGenerateNewTaxInvoice && defaultProvider;
  const hasTaxInvoices = taxInvoiceList.length > 0;
  const isBoleta =
    taxInvoiceInformation?.taxInvoiceType?.name === TAX_INVOICE_TYPE_BOLETA;

  const receiverTypeLabel = isBoleta ? 'Nombres' : 'Razón Social';
  const receiverCodeLabel = isBoleta ? 'DNI' : 'RUC';
  const documentTypeLabel =
    taxInvoiceInformation?.taxInvoiceType?.label || 'Documento';

  const emitButtonDisabled =
    !canEmitDocument || emitLoading || shouldGenerateAutomatically;

  const formDefaultValues = taxInvoiceInformation
    ? {
        address: taxInvoiceInformation.address,
        admin1Id: taxInvoiceInformation.admin1,
        admin2Id: taxInvoiceInformation.admin2,
        admin3Id: taxInvoiceInformation.admin3,
        taxInvoiceType: taxInvoiceInformation.taxInvoiceType?.name,
        companyName: taxInvoiceInformation.taxReceiverName,
        ruc: taxInvoiceInformation.taxReceiverCode,
      }
    : undefined;

  // Auto-refresh for automatic generation
  useEffect(() => {
    if (triesRef.current >= MAX_AUTO_REFRESH_TRIES) {
      showToast({
        type: 'danger',
        title: 'Error al generar documento',
        message:
          'Ocurrió un error al intentar generar el documento. Por favor, recargue la página.',
      });
      return;
    }

    if (!order) return;

    const lastInvoice = taxInvoiceList[taxInvoiceList.length - 1];

    if (taxInvoiceList.length !== 0 && lastInvoice.status === 'ACCEPTED')
      return;
    if (!shouldGenerateAutomatically) return;

    triesRef.current += 1;

    const interval = setInterval(() => {
      refetch();
    }, AUTO_REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [order, refetch, shouldGenerateAutomatically, showToast, taxInvoiceList]);

  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  const handleOpenModal = (event: SyntheticEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setFormModalOpen(true);
  };

  const handleCloseModal = () => {
    setFormModalOpen(false);
  };

  const handleFormSubmit = data => {
    const countryCode = country?.code || DEFAULT_COUNTRY_CODE;

    const taxInvoiceData = {
      taxInvoiceType: data[ORDER_TAX_INVOICE_FORM_FIELDS.taxInvoiceType],
      taxReceiverName: data[ORDER_TAX_INVOICE_FORM_FIELDS.companyName],
      address: {
        address: data[ORDER_TAX_INVOICE_FORM_FIELDS.address],
        admin1: data[ORDER_TAX_INVOICE_FORM_FIELDS.admin1],
        admin2: data[ORDER_TAX_INVOICE_FORM_FIELDS.admin2],
        admin3: data[ORDER_TAX_INVOICE_FORM_FIELDS.admin3],
        country: countryCode,
      },
      taxReceiverCode: data[ORDER_TAX_INVOICE_FORM_FIELDS.ruc],
      country: countryCode,
    };

    setSavingForm(true);
    editOrderTaxInvoiceData(order.id, taxInvoiceData)
      .then(response => {
        if (!response) return;
        updateOrderTaxInvoiceInformation({
          taxReceiverCode: response.taxReceiverCode,
          taxReceiverName: response.taxReceiverName,
          address: response.address,
          admin1: response.admin1,
          admin2: response.admin2,
          admin3: response.admin3,
          taxInvoiceType: response.taxInvoiceType,
          id: response.id,
        });
        handleCloseModal();
        refetch();
      })
      .finally(() => setSavingForm(false));
  };

  const handleEmit = () => {
    if (!order) return;
    emitOrderTaxInvoice(order.id).then(() => {
      refetch();
    });
  };

  if (noTaxInvoice) return null;

  return (
    <div className={classes.container}>
      <div className={classes.header} onClick={handleToggleExpand}>
        <Typography className={classes.title}>
          <LocalAtm fontSize="small" />
          Facturación
        </Typography>
        <div className={classes.headerActions}>
          <CustomButton
            startIcon={<Edit />}
            onClick={handleOpenModal}
            size="small"
          >
            {taxInvoiceInformation ? 'Modificar' : 'Agregar'}
          </CustomButton>
          {expanded && <ExpandLess />}
          {!expanded && <ExpandMore />}
        </div>
      </div>

      <Collapse in={expanded}>
        <div className={classes.content}>
          {/* Skeleton for initial loading */}
          {initialLoading ? (
            <div className={classes.skeletonContainer}>
              <Skeleton
                variant="text"
                width="70%"
                height={20}
                className={classes.skeletonLine}
              />
              <Skeleton
                variant="text"
                width="60%"
                height={20}
                className={classes.skeletonLine}
              />
              <Skeleton
                variant="text"
                width="50%"
                height={20}
                className={classes.skeletonLine}
              />
            </div>
          ) : (
            <>
              {/* No data state */}
              {!taxInvoiceInformation && (
                <Typography className={classes.noDataText} variant="subtitle2">
                  No hay datos de facturación
                </Typography>
              )}

              {/* Tax invoice information */}
              {taxInvoiceInformation && (
                <>
                  <Divider className={classes.divider} />
                  <Typography className={classes.contentText}>
                    <span className={classes.bold}>Tipo de comprobante:</span>{' '}
                    {taxInvoiceInformation.taxInvoiceType.label}
                  </Typography>
                  <Typography className={classes.contentText}>
                    <span className={classes.bold}>{receiverTypeLabel}:</span>{' '}
                    {taxInvoiceInformation.taxReceiverName}
                  </Typography>
                  <Typography className={classes.contentText}>
                    <span className={classes.bold}>{receiverCodeLabel}:</span>{' '}
                    {taxInvoiceInformation.taxReceiverCode}
                  </Typography>
                </>
              )}

              {/* Generate section */}
              {showGenerateSection && (
                <div className={classes.generateCard}>
                  <header className={classes.generateHeader}>
                    <LocalAtm fontSize="small" />
                    <Typography className={classes.generateHeaderLabel}>
                      {documentTypeLabel}
                    </Typography>
                  </header>
                  <div className={classes.generateOptionsContainer}>
                    <InlineLoader
                      size={20}
                      className={classes.generateLoader}
                      loading={shouldGenerateAutomatically || emitLoading}
                    />
                    <ButtonGroup
                      variant="text"
                      fullWidth
                      className={classes.generateFooter}
                    >
                      <CustomButton
                        startIcon={<Edit />}
                        fullWidth
                        variant="text"
                        className={classes.editButton}
                        onClick={handleOpenModal}
                      >
                        Editar
                      </CustomButton>
                      <CustomButton
                        startIcon={<AddCircleOutline />}
                        fullWidth
                        disabled={emitButtonDisabled}
                        variant="text"
                        onClick={handleEmit}
                        className={classes.emitButton}
                      >
                        Emitir
                      </CustomButton>
                    </ButtonGroup>
                  </div>
                  {!canEmitDocument && (
                    <Typography
                      variant="caption"
                      className={classes.caption}
                      style={{
                        paddingInline: '.5rem',
                      }}
                    >
                      Solo se puede emitir una factura a partir del estado
                      Pagado
                    </Typography>
                  )}
                  {shouldGenerateAutomatically && (
                    <Typography
                      variant="caption"
                      className={classes.caption}
                      style={{
                        paddingInline: '.5rem',
                      }}
                    >
                      El documento se generará automáticamente
                    </Typography>
                  )}
                </div>
              )}

              {/* Invoice list */}
              {hasTaxInvoices && (
                <div className={classes.invoiceListContainer}>
                  <InvoiceSection
                    taxInvoiceList={taxInvoiceList}
                    onRefetch={refetch}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </Collapse>

      <FormProvider {...methods}>
        <CustomModal
          open={formModalOpen}
          onClose={handleCloseModal}
          title="Datos de Facturación"
          className={classes.modalContainer}
        >
          {savingForm && (
            <div className={classes.loader}>
              <InlineLoader loading size={36} />
            </div>
          )}
          <TaxInvoiceInformationForm defaultValues={formDefaultValues} />
          <CustomButton
            onClick={methods.handleSubmit(handleFormSubmit)}
            variant="contained"
            fullWidth
            className={classes.saveButton}
          >
            Guardar
          </CustomButton>
        </CustomModal>
      </FormProvider>
    </div>
  );
}
